/**
 * Страница управления каналами
 * Автор: Cursor Generator
 * Описание: CRUD операции для Telegram каналов
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import MagneticButton from '../components/MagneticButton';
import { showToast, initToast } from '../components/Toast';
import { API_URL } from '../utils/api.js';

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    channel_username: '',
    bot_token: '',
    is_target: true,
  });

  useEffect(() => {
    initToast();
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API_URL}/channels`);
      setChannels(res.data.channels || []);
    } catch (error) {
      console.error('Ошибка при загрузке каналов:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // user_id_new берется автоматически из сессии через requireUserAuth middleware
      const response = await axios.post(`${API_URL}/channels`, formData);
      
      if (response.data.error) {
        const errorMsg = response.data.message || response.data.error;
        const details = response.data.details ? `\n\nДетали: ${response.data.details}` : '';
        showToast(`❌ Ошибка при создании канала: ${errorMsg}${details}`, 'error');
        console.error('Детали ошибки:', response.data);
        return;
      }
      
      showToast('✅ Канал успешно создан!', 'success');
      setShowForm(false);
      setFormData({ channel_username: '', bot_token: '', is_target: true });
      fetchChannels();
    } catch (error) {
      console.error('Ошибка при создании канала:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      const errorDetails = error.response?.data?.details ? `\n\nДетали: ${error.response.data.details}` : '';
      showToast(`❌ Ошибка при создании канала: ${errorMessage}${errorDetails}`, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Удалить канал?')) return;
    try {
      const response = await axios.delete(`${API_URL}/channels/${id}`);
      
      if (response.data.error) {
        showToast(`❌ Ошибка при удалении канала: ${response.data.error}. ${response.data.message || ''}`, 'error');
        return;
      }
      
      showToast('✅ Канал успешно удален!', 'success');
      fetchChannels();
    } catch (error) {
      console.error('Ошибка при удалении канала:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      showToast(`❌ Ошибка при удалении канала: ${errorMessage}`, 'error');
    }
  };

  if (loading) {
    return <div className="text-center">Загрузка...</div>;
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white tracking-tighter">📢 Управление каналами</h2>
        <MagneticButton variant="primary" onClick={() => setShowForm(!showForm)}>
          ➕ Добавить канал
        </MagneticButton>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 shadow-cartoon mb-8"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username канала</label>
              <input
                type="text"
                value={formData.channel_username}
                onChange={(e) =>
                  setFormData({ ...formData, channel_username: e.target.value })
                }
                className="w-full px-4 py-2 border-2 border-cartoon-blue rounded-2xl focus:outline-none focus:ring-2 focus:ring-cartoon-blue"
                placeholder="@channel"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Bot Token (токен бота-администратора канала)
              </label>
              <input
                type="text"
                value={formData.bot_token}
                onChange={(e) => setFormData({ ...formData, bot_token: e.target.value })}
                className="w-full px-4 py-2 border-2 border-cartoon-blue rounded-2xl focus:outline-none focus:ring-2 focus:ring-cartoon-blue"
                placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                💡 Токен бота, который является администратором этого канала. Этот бот будет использоваться для публикации постов в канал.
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_target}
                onChange={(e) => setFormData({ ...formData, is_target: e.target.checked })}
                className="mr-2"
              />
              <label>Целевой канал для публикации</label>
            </div>
            <div className="flex space-x-2">
              <MagneticButton type="submit" variant="primary">
                Создать
              </MagneticButton>
              <MagneticButton
                type="button"
                variant="warning"
                onClick={() => setShowForm(false)}
              >
                Отмена
              </MagneticButton>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-4">
        {channels.map((channel) => (
          <motion.div
            key={channel.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-6 shadow-cartoon"
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold">{channel.channel_username}</h3>
                <div className="text-gray-500 text-sm space-y-1">
                  <p>
                    {channel.is_target ? '✅ Целевой канал' : '📋 Мониторинг'}
                  </p>
                  <p>
                    {channel.bot_token ? '🤖 Бот-администратор настроен' : '⚠️ Бот-администратор не настроен'}
                  </p>
                </div>
              </div>
              <MagneticButton
                variant="warning"
                onClick={() => handleDelete(channel.id)}
              >
                🗑️ Удалить
              </MagneticButton>
            </div>
          </motion.div>
        ))}
      </div>
      </div>
    </div>
  );
}


